import { useParams } from 'react-router-dom';
import { useClubHost } from '@/contexts/ClubHostContext';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { usePublicOrgProfile } from '@/queries/publicOrgProfile';

function useOrgProfileClubSlug(): {
  clubSlug?: string;
  isResolving: boolean;
} {
  const { slug: legacyClubSlug } = useParams<{ slug?: string }>();
  const { pathSlug, host, siblingClub, club } = useClubHost();
  const { clubSlug: orgHostClubSlug, club: orgHostClub, host: orgHost, loading: orgHostLoading } =
    useOrgHost();
  const orgSlug = useOrgSlug();
  const { profile: contextProfile, isLoading: contextLoading, orgSlug: contextOrgSlug } =
    useOrgPublicProfile();

  const needsOrgProfile =
    !legacyClubSlug &&
    !orgHostClubSlug &&
    !orgHostClub?.slug &&
    !club?.slug &&
    !(host.mode === 'subdomain' && (pathSlug || siblingClub?.slug)) &&
    !!orgSlug &&
    !contextProfile &&
    !(contextLoading && contextOrgSlug);

  const { profile, isLoading: profileLoading } = usePublicOrgProfile({
    slug: orgSlug,
    subdomainLabel: orgHost.mode === 'subdomain' ? orgHost.subdomainLabel : undefined,
    hostLoading: orgHost.mode === 'subdomain' && orgHostLoading,
    enabled: needsOrgProfile,
  });

  if (contextProfile) {
    return {
      clubSlug:
        contextProfile.clubSlug ??
        (contextProfile.modules.hasClub && orgSlug ? orgSlug : undefined),
      isResolving: false,
    };
  }

  if (contextLoading && contextOrgSlug) {
    return { clubSlug: undefined, isResolving: true };
  }

  if (!needsOrgProfile) {
    return { clubSlug: undefined, isResolving: false };
  }

  if (profileLoading && !profile) {
    return { clubSlug: undefined, isResolving: true };
  }

  const clubSlug =
    profile?.clubSlug ?? (profile?.modules.hasClub && orgSlug ? orgSlug : undefined);

  return { clubSlug, isResolving: false };
}

/**
 * Resolved path slug for club-scoped API calls and path-mode links.
 * On subdomains, uses the loaded club's slug (not the hostname label).
 * On unified org routes (`/club-academy/:orgSlug`), resolves from the public org profile.
 */
export function useClubSlug(): string | undefined {
  const { slug: legacyClubSlug } = useParams<{ slug?: string }>();
  const { pathSlug, host, siblingClub, club } = useClubHost();
  const { clubSlug: orgHostClubSlug, club: orgHostClub } = useOrgHost();
  const { clubSlug: orgProfileClubSlug } = useOrgProfileClubSlug();

  if (legacyClubSlug) return legacyClubSlug;
  if (host.mode === 'subdomain' && pathSlug) return pathSlug;
  if (host.mode === 'subdomain' && siblingClub?.slug) return siblingClub.slug;
  if (orgHostClubSlug) return orgHostClubSlug;
  if (orgHostClub?.slug) return orgHostClub.slug;
  if (club?.slug) return club.slug;
  if (orgProfileClubSlug) return orgProfileClubSlug;

  return pathSlug;
}

/** True while unified org profile is loading to resolve the club slug. */
export function useClubSlugResolving(): boolean {
  return useOrgProfileClubSlug().isResolving;
}
